import React, { useEffect, useMemo, useState } from 'react';
import { FiFileText } from 'react-icons/fi';
import { MdNavigateNext } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { RootState } from '../../../../../../store/rootReducer';
import { dealTypes } from '../../../../../login/utils/Status';
import { DataRoom } from '../../../../deals/redux/types';
import { ReportingObligation } from '../../../redux/types';
import ExecutedAgreements from '../ExecutedAgreements';
import ReportingObligations from '../ReportingObligation';
import ReportingOrUpdates from '../ReportingOrUpdates';

type LocationType = {
    date: string | undefined;
};

const InvestmentDocuments: React.FC = () => {
    const investmentUUID = useParams().uuid;
    const location = useLocation();
    const dealUUID = useSelector((state: RootState) => state.investments.investmentDetails?.dealUUID);
    const bookingUUID = useSelector((state: RootState) => state.investments.investmentDetails?.bookingUUID);

    const executedAgreements = useSelector((state: RootState) => state.investments.executedAgreements);
    const documents = useSelector((state: RootState) => state.investments.investmentDetails?.documents);
    const ppm = useSelector((state: RootState) => state.investments.ppm);
    const updates = useSelector((state: RootState) => state.investments.updates);
    const investmentDetails = useSelector((state: RootState) => state.investments.investmentDetails);
    const reportingObligations = useSelector((state: RootState) => state.investments.reportingObligations);

    const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
    const [showDate, setDate] = useState(true);

    const stateData: { date: string | undefined } = location && (location.state as LocationType);

    const reportingObligationList = useMemo(() => {
        if (bookingUUID) {
            let res: ReportingObligation[] = [];
            reportingObligations.forEach((r) => {
                if (r.reports && r.reports[0] && r.reports[0].bookingUUID === bookingUUID) {
                    res.push(r);
                }
            });
            return res;
        }
        return reportingObligations;
    }, [bookingUUID, reportingObligations]);

    useEffect(() => {
        if (stateData?.date) {
            setSelectedType('reportingObligation');
        }
    }, [stateData?.date]);

    const dealType = useMemo(() => {
        const type = investmentDetails?.deal?.info.summary?.dealType;
        const bookingUUID = investmentDetails?.bookingUUID;
        if (type) {
            return type;
        }
        if (bookingUUID) {
            return dealTypes.debt;
        }
        return undefined;
    }, [investmentDetails]);

    const InvestmentDocuments = useMemo(() => {
        let list: DataRoom[] = [];
        if (documents && documents?.length > 0 && dealUUID) {
            documents.forEach((d) => {
                list.push({
                    name: d.title,
                    type: 'File',
                    investmentUUID: investmentUUID,
                    dealUUID: dealUUID,
                    group: 'ExecutedAgreements',
                    isDownloadable: false,
                    uuid: '',
                });
            });
        }
        return list;
    }, [documents, investmentUUID, dealUUID]);

    const PPM = useMemo(() => {
        let list: DataRoom[] = [];
        if (ppm && ppm.ppmNumber && dealUUID) {
            list.push({
                name: `Fund PPM`,
                type: 'File',
                investmentUUID: investmentUUID,
                dealUUID: dealUUID,
                group: 'PPM',
                isDownloadable: false,
                uuid: '',
            });
        }
        return list;
    }, [ppm, investmentUUID, dealUUID]);

    const docs = useMemo(() => {
        let doctype: {
            name: string;
            type: 'executedAgreements' | 'updates' | 'reportingObligation';
            folder: number;
            file: number;
        }[] = [];
        if (dealUUID && dealUUID !== 'undefined') {
            doctype = [
                {
                    name: 'Executed Agreements',
                    type: 'executedAgreements',
                    folder: (executedAgreements || [])
                        .concat((InvestmentDocuments || []).concat(PPM || []))
                        .filter((d) => d.type === 'Folder').length,
                    file: (executedAgreements || [])
                        .concat((InvestmentDocuments || []).concat(PPM || []))
                        .filter((d) => d.type === 'File').length,
                },
                {
                    name: 'Updates',
                    type: 'updates',
                    file:
                        updates?.filter((d) => d.type === 'File').length ||
                        updates?.filter((d) => d.type !== 'File' && d.type !== 'Folder').length ||
                        0,
                    folder: updates?.filter((d) => d.type === 'Folder').length || 0,
                },
            ];
        }
        if (dealType === dealTypes.debt) {
            let count = 0;
            reportingObligationList.forEach((r) => {
                [...r.reports]
                    .filter((a, i, self) => self.findIndex((s) => s.obligationType === a.obligationType) === i)
                    .forEach((i) => {
                        count = count + (i.uploadDetails || []).length;
                    });
            });
            doctype.unshift({
                name: 'Reporting Obligations',
                type: 'reportingObligation',
                file: count,
                folder: 0,
            });
        }
        return doctype;
    }, [InvestmentDocuments, PPM, executedAgreements, updates, reportingObligationList, dealType, dealUUID]);

    const component = useMemo(() => {
        switch (selectedType) {
            case 'reportingObligation':
                return <ReportingObligations setSelectedType={setSelectedType} showDate={showDate} setDate={setDate} />;
            case 'executedAgreements':
                return <ExecutedAgreements setSelectedType={setSelectedType} />;
            case 'updates':
                return <ReportingOrUpdates setSelectedType={setSelectedType} />;
            default:
                return null;
        }
    }, [selectedType, showDate]);

    return (
        <>
            {!selectedType && (
                <div className="px-6 pb-4">
                    {docs.map((doc, index) => (
                        <div
                            key={`${doc.type}_${index}`}
                            className=" px-4 py-4 flex items-center justify-between mb-3 cursor-pointer bg-[#F5F4F2]"
                            onClick={() => setSelectedType(doc.type)}
                        >
                            <div className="flex items-center">
                                <FiFileText size={'2.2rem'} color="#26AE60" className="mr-2" />
                                <div className="flex flex-col WorkSans-Semibold font-semibold text-sm">
                                    <p className="text-[#20222B]">{doc.name}</p>
                                    {(doc.file > 0 || doc.folder > 0) && (
                                        <p className="WorkSans text-sm font-normal text-[#999999]">{`${doc.folder} Folders |  ${doc.file} Files`}</p>
                                    )}
                                    {doc.file === 0 && doc.folder === 0 && (
                                        <p className="WorkSans text-sm font-normal text-[#999999]">Empty</p>
                                    )}
                                </div>
                            </div>
                            <MdNavigateNext size={'1.6rem'} color="#182D4F" />
                        </div>
                    ))}
                </div>
            )}
            {selectedType && component}
        </>
    );
};

export default InvestmentDocuments;
